import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, Link2, Lock, AlertTriangle } from 'lucide-react';

export default function SharedContentPage() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<'loading' | 'success' | 'error' | 'password'>('loading');
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) { setState('error'); setError('Invalid share link'); return; }
    (async () => {
      try {
        const res = await fetch('/_api/user-media/share-link', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'access', shareToken: token }),
        });
        const json = await res.json();
        if (res.ok) {
          setData(json);
          setState('success');
        } else {
          setError(json.error || 'Link not found or expired');
          setState(json.error?.includes('password') ? 'password' : 'error');
        }
      } catch (e: any) {
        setError(e.message || 'Failed to load');
        setState('error');
      }
    })();
  }, [token]);

  useEffect(() => {
    const root = document.getElementById('root');
    if (root) { root.style.overflow = 'auto'; root.style.height = 'auto'; }
    document.body.style.overflow = 'auto';
    return () => { if (root) { root.style.overflow = ''; root.style.height = ''; } document.body.style.overflow = ''; };
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0f', color: '#fff', display: 'flex', flexDirection: 'column' }}>
      <nav style={{ padding: '16px 24px', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', color: '#fff' }}>
          <img src="/logo.png" alt="Tasking.tech" style={{ width: 28, height: 28 }} />
          <span style={{ fontWeight: 700, fontSize: '1.1rem' }}>tasking<span style={{ color: '#f97316' }}>.tech</span></span>
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Link to="/login" style={{ color: '#999', textDecoration: 'none', fontSize: '0.85rem' }}>Log in</Link>
          <Link to="/login?signup=true" style={{ padding: '6px 16px', background: '#f97316', color: '#fff', borderRadius: 6, textDecoration: 'none', fontSize: '0.85rem', fontWeight: 600 }}>Sign Up</Link>
        </div>
      </nav>

      <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
        {state === 'loading' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#f97316' }} />
            <p style={{ color: '#999' }}>Loading shared content...</p>
          </div>
        )}

        {state === 'password' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, textAlign: 'center', maxWidth: 360 }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(249,115,22,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Lock size={28} style={{ color: '#f97316' }} />
            </div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>Password Protected</h2>
            <p style={{ color: '#999', margin: 0 }}>Enter the password to access this shared content.</p>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const pw = (e.currentTarget.elements.namedItem('pw') as HTMLInputElement).value;
                try {
                  const res = await fetch('/_api/user-media/share-link', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ action: 'access', shareToken: token, password: pw }),
                  });
                  const json = await res.json();
                  if (res.ok) { setData(json); setState('success'); }
                  else { setError(json.error || 'Incorrect password'); setState(json.error?.includes('password') ? 'password' : 'error'); }
                } catch { setError('Failed to verify password'); }
              }}
              style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}
            >
              <input
                name="pw"
                type="password"
                placeholder="Enter password"
                autoFocus
                style={{
                  padding: '10px 14px', borderRadius: 8, border: '1px solid #333',
                  background: '#111118', color: '#fff', fontSize: '0.95rem', outline: 'none',
                }}
              />
              {error && error !== 'Incorrect password' ? null : error ? (
                <p style={{ color: '#ef4444', fontSize: '0.8rem', margin: 0 }}>{error}</p>
              ) : null}
              <button
                type="submit"
                style={{
                  padding: '10px 20px', borderRadius: 8, border: 'none',
                  background: '#f97316', color: '#fff', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer',
                }}
              >
                Unlock
              </button>
            </form>
          </div>
        )}

        {state === 'error' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AlertTriangle size={28} style={{ color: '#ef4444' }} />
            </div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>Link unavailable</h2>
            <p style={{ color: '#999', maxWidth: 400, margin: 0 }}>{error}</p>
            <Link to="/" style={{ color: '#f97316', textDecoration: 'none', fontSize: '0.85rem' }}>Go to Tasking.tech</Link>
          </div>
        )}

        {state === 'success' && data && (
          <div style={{ maxWidth: 900, width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
              <Link2 size={18} style={{ color: '#f97316' }} />
              <h1 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>Shared Content</h1>
            </div>

            {(data.asset?.fileType?.startsWith('image/') || /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(data.asset?.fileName || '')) && (
              <img
                src={data.asset.url}
                alt={data.asset.fileName}
                style={{ maxWidth: '100%', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)' }}
              />
            )}

            {(data.asset?.fileType === 'application/pdf' || data.asset?.fileName?.endsWith('.pdf')) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {data.asset.thumbnailUrl && (
                  <img
                    src={data.asset.thumbnailUrl}
                    alt="PDF Cover"
                    style={{ maxWidth: 300, borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 4px 20px rgba(0,0,0,0.3)', marginBottom: 8 }}
                  />
                )}
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <a
                    href={data.asset.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 24px', background: '#f97316', color: '#fff', borderRadius: 8, textDecoration: 'none', fontWeight: 600, fontSize: '1rem' }}
                  >
                    View PDF
                  </a>
                  <a
                    href={data.asset.url}
                    download={data.asset.fileName}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 24px', background: '#222', color: '#fff', borderRadius: 8, textDecoration: 'none', fontWeight: 600, fontSize: '1rem', border: '1px solid #333' }}
                  >
                    Download
                  </a>
                </div>
              </div>
            )}

            {data.asset?.fileName && (
              <div style={{ marginTop: 16, padding: '16px 20px', background: '#111118', borderRadius: 10, border: '1px solid rgba(255,255,255,0.1)' }}>
                <p style={{ margin: 0, fontWeight: 600 }}>{data.asset.fileName}</p>
                {data.asset.fileSize && (
                  <p style={{ margin: '4px 0 0', color: '#999', fontSize: '0.8rem' }}>
                    {(data.asset.fileSize / 1024).toFixed(1)} KB
                  </p>
                )}
                {data.asset.url && (
                  <a
                    href={data.asset.url}
                    download={data.asset.fileName}
                    style={{ display: 'inline-block', marginTop: 12, padding: '8px 16px', background: '#f97316', color: '#fff', borderRadius: 8, textDecoration: 'none', fontSize: '0.85rem', fontWeight: 600 }}
                  >
                    Download
                  </a>
                )}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
